import { createStore } from '@shared/utils/state/state-factory'
import { flowService } from 'v2/shared/services/flow-service'
import { EvalEvents } from 'v2/utils/event-bus/eval-events'
import { agentLogger } from 'v2/utils/logger'
import { highlightNodeAndEdges } from '../../run-info/utils'
import { useFlowStore } from '../../store'
import { FlowNodeTypes } from '../../types'
import {
  type EvalActions,
  type EvalNode,
  type EvalResult,
  type EvalState,
  EvalStatus,
  type ResultPayload,
  type SkippedNodePayload,
  type TestCasePayload,
} from './types'
import { updateEvalStatusOnNodes } from './utils'

const initialState: EvalState = {
  currentEvalId: null,
  currentRunId: null,
  currentlyTestingNodeUuid: null,
  events: [],
  evalResults: {
    nodes: [],
    summary: {
      totalTests: 0,
      passed: 0,
      failed: 0,
      testing: 0,
    },
  },
  isEvalRunning: false,
  isPaused: false,
  sseController: null,
}

// Helper: Get node info from flow
const getNodeInfo = (nodeUuid: string): { id: string; name: string; type: FlowNodeTypes } => {
  const flowState = useFlowStore.getState()
  const flowInstance = flowState.flowInstance
  const node = flowInstance?.getNode(nodeUuid)

  if (!node) return { id: nodeUuid, name: 'AI Agent', type: FlowNodeTypes.ai_agent_v2 }

  return {
    id: node.id,
    name: node.data.config.name || 'AI Agent',
    type: node.type as FlowNodeTypes,
  }
}

// Helper: Calculate node status based on test cases
const calculateNodeStatus = (
  testCases: Array<{
    status: EvalStatus
  }>,
): EvalStatus => {
  if (testCases.length === 0) return EvalStatus.Pending

  const anyTesting = testCases.some((tc) => tc.status === 'testing')
  if (anyTesting) return EvalStatus.Testing

  const allComplete = testCases.every((tc) => tc.status === 'passed' || tc.status === 'failed')
  if (!allComplete) return EvalStatus.Pending

  const anyFailed = testCases.some((tc) => tc.status === 'failed')
  if (anyFailed) return EvalStatus.Failed

  return EvalStatus.Passed
}

// Helper: Update summary stats
const updateSummary = (evalResults: EvalResult) => {
  const allTests = evalResults.nodes.flatMap((n) => n.testCases)
  evalResults.summary = {
    totalTests: allTests.length,
    passed: allTests.filter((t) => t.status === 'passed').length,
    failed: allTests.filter((t) => t.status === 'failed').length,
    testing: allTests.filter((t) => t.status === 'testing').length,
  }

  // Update node statuses while respecting skipped/testing overrides
  for (const node of evalResults.nodes) {
    normalizeNodeStatus(node)
  }
}

const getOrCreateEvalNode = (
  state: EvalState,
  nodeUuid: string,
  runId: number,
  options?: { initialStatus?: EvalStatus; expand?: boolean },
) => {
  const existingNode = state.evalResults.nodes.find(
    (n) => n.nodeUuid === nodeUuid && n.runId === runId,
  )
  if (existingNode) {
    return existingNode
  }

  const nodeInfo = getNodeInfo(nodeUuid)
  const newNode = {
    nodeId: nodeInfo.id,
    nodeUuid,
    nodeName: nodeInfo.name,
    nodeType: nodeInfo.type as FlowNodeTypes,
    testCases: [],
    isExpanded: options?.expand ?? true,
    status: options?.initialStatus,
    runId,
    skipReason: undefined,
  }

  state.evalResults.nodes.push(newNode)
  return newNode
}

const markNodeTesting = (node: EvalNode, state: EvalState) => {
  if (node.status !== EvalStatus.Skipped) {
    node.status = EvalStatus.Testing
  }
  node.skipReason = undefined
  state.currentlyTestingNodeUuid = node.nodeUuid
}

const normalizeNodeStatus = (node: EvalNode) => {
  if (node.status === EvalStatus.Skipped) return
  node.skipReason = undefined
  if (node.testCases.length === 0 && node.status === EvalStatus.Testing) return

  node.status = calculateNodeStatus(node.testCases)
}

export const useEvalStore = createStore<EvalState & EvalActions>('eval', (set) => ({
  ...initialState,

  setCurrentEvalId: (id) => set({ currentEvalId: id }),

  setCurrentRunId: (id) => set({ currentRunId: id }),

  addEvent: (event) => {
    set((state) => {
      // Allow lifecycle events (STARTED, ERROR, CANCELLED) to bypass the isEvalRunning guard
      const isLifecycleEvent =
        event.type === EvalEvents.STARTED ||
        event.type === EvalEvents.ERROR ||
        event.type === EvalEvents.CANCELLED
      if (!state.isEvalRunning && !isLifecycleEvent) return

      // Extract run_id from event payload
      const eventRunId =
        event.payload && typeof event.payload === 'object' && 'run_id' in event.payload
          ? (event.payload as { run_id: number }).run_id
          : 0

      // Update currentRunId to the latest run_id we see
      // This handles the case where backend sends events with incrementing run_ids
      if (eventRunId !== null && (state.currentRunId === null || eventRunId > state.currentRunId)) {
        state.currentRunId = eventRunId
        agentLogger.info('Updated currentRunId from event', { eventRunId, eventType: event.type })
      }

      // Store raw event
      state.events.push(event)

      // Transform based on event type
      if (event.type === EvalEvents.DATASET_GENERATION_STARTED_NODE) {
        const payload = event.payload as { node_uuid?: string }
        const nodeUuid = payload?.node_uuid

        if (!nodeUuid) {
          agentLogger.warn('DATASET_GENERATION_STARTED_NODE event missing node_uuid', { payload })
          return
        }

        const node = getOrCreateEvalNode(state, nodeUuid, eventRunId, {
          initialStatus: EvalStatus.Testing,
          expand: true,
        })

        markNodeTesting(node, state)

        updateSummary(state.evalResults)
        agentLogger.debug('Dataset generation started for node', {
          nodeUuid,
          runId: eventRunId,
        })
      }

      if (event.type === EvalEvents.DATASET_GENERATION_TEST_CASE) {
        const testCase = (event.payload as TestCasePayload)?.test_case
        if (!testCase) return

        // Find or create node
        const node = getOrCreateEvalNode(state, testCase.node_uuid, eventRunId, {
          initialStatus: EvalStatus.Testing,
          expand: true,
        })

        // Add test case - use test_id if available, otherwise generate one
        const testCaseId =
          testCase.test_id ||
          `${testCase.node_uuid}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

        // Check if test case already exists (deduplication for resume scenarios)
        const existingTestCaseIndex = node.testCases.findIndex((tc) => tc.id === testCaseId)
        if (existingTestCaseIndex !== -1) {
          const existingTestCase = node.testCases[existingTestCaseIndex]

          // If existing test case has results, don't overwrite
          if (existingTestCase.status === 'passed' || existingTestCase.status === 'failed') {
            agentLogger.debug('Test case already has results, skipping duplicate', {
              test_id: testCaseId,
              name: testCase.name,
              status: existingTestCase.status,
            })
            return
          }

          // If still in testing state, it's a duplicate generation event
          agentLogger.debug('Test case already exists in testing state, skipping duplicate', {
            test_id: testCaseId,
            name: testCase.name,
          })
          return
        }

        // Test case doesn't exist, add it
        node.testCases.push({
          id: testCaseId,
          name: testCase.name,
          nodeUuid: testCase.node_uuid,
          userMessage: testCase.user_message,
          expectedVariables: testCase.expected_variables || {},
          nodeVariables: testCase.node_variables || {},
          conversationHistory: (testCase.conversation_history || []).map((entry) => ({
            message: entry.message,
            role: entry.role,
            intent_chosen: entry.intent_chosen,
            variables_extracted: entry.variables_extracted,
            previous_node: entry.previous_node,
            new_node: entry.new_node,
            tool_name: entry.tool_name,
            from_node_uuid: entry.from_node_uuid,
            to_node_uuid: entry.to_node_uuid,
            currentNodeUuid: entry.current_node_uuid,
          })),
          rigidityCategory: testCase.rigidity_category,
          status: EvalStatus.Testing, // Start as testing
          runId: eventRunId,
        })

        // Update currently testing node
        markNodeTesting(node, state)

        updateSummary(state.evalResults)
        agentLogger.debug('Test case added', {
          name: testCase.name,
          test_id: testCaseId,
          nodeUuid: testCase.node_uuid,
        })
      }

      if (event.type === EvalEvents.TESTS_STARTED_FOR_NODE) {
        const payload = event.payload as { node_uuid?: string }
        const nodeUuid = payload?.node_uuid

        if (!nodeUuid) {
          agentLogger.warn('TESTS_STARTED_FOR_NODE event missing node_uuid', { payload })
          return
        }

        const node = getOrCreateEvalNode(state, nodeUuid, eventRunId, {
          initialStatus: EvalStatus.Testing,
          expand: true,
        })

        markNodeTesting(node, state)

        updateSummary(state.evalResults)
        agentLogger.debug('Node tests started', {
          nodeUuid,
          runId: eventRunId,
        })
      }

      if (event.type === EvalEvents.NODE_QUEUED) {
        const payload = event.payload as { node_uuid?: string; node_index?: number }
        const nodeUuid = payload?.node_uuid

        if (!nodeUuid) {
          agentLogger.warn('NODE_QUEUED event missing node_uuid', { payload })
          return
        }

        // Create node with Pending status (will transition to Testing when picked up)
        getOrCreateEvalNode(state, nodeUuid, eventRunId, {
          initialStatus: EvalStatus.Pending,
          expand: false,
        })

        updateSummary(state.evalResults)
        agentLogger.debug('Node queued for processing', {
          nodeUuid,
          nodeIndex: payload?.node_index,
          runId: eventRunId,
        })
      }

      if (event.type === EvalEvents.RESULT) {
        const payload = event.payload as ResultPayload
        const eventData = payload?.event_data || payload

        const {
          test_id,
          test_name,
          success,
          metrics,
          metrics_data,
          actual_response,
          expected_response,
        } = eventData || {}

        // Use metrics_data if metrics is not available
        const metricsArray = metrics || metrics_data || []

        // Try to match by test_id first, then fall back to test_name
        let foundTestCase = false

        if (test_id) {
          // Match by test_id (exact match or starts with)
          for (const node of state.evalResults.nodes) {
            const testCase = node.testCases.find(
              (tc) => tc.id === test_id || tc.id.includes(test_id),
            )
            if (testCase) {
              testCase.status = success ? EvalStatus.Passed : EvalStatus.Failed
              testCase.success = success
              testCase.metrics = metricsArray
              testCase.actualResponse = actual_response
              testCase.expectedResponse = expected_response
              testCase.runId = eventRunId
              agentLogger.debug('Test case result updated by test_id', {
                test_id,
                testCaseId: testCase.id,
                success,
                metricsCount: metricsArray.length,
              })
              foundTestCase = true
              break
            }
          }
        }

        if (!foundTestCase) {
          agentLogger.warn('Could not find test case for result', { test_id, test_name })
        }

        updateSummary(state.evalResults)
      }

      if (event.type === EvalEvents.NODE_EVALUATION_SKIPPED) {
        const payload = event.payload as SkippedNodePayload
        const { node_uuid, reason } = payload

        if (!node_uuid) {
          agentLogger.warn('NODE_EVALUATION_SKIPPED event missing node_uuid', { payload })
          return
        }

        // Find or create node
        let node = state.evalResults.nodes.find(
          (n) => n.nodeUuid === node_uuid && n.runId === eventRunId,
        )
        if (!node) {
          const nodeInfo = getNodeInfo(node_uuid)
          node = {
            nodeId: nodeInfo.id,
            nodeUuid: node_uuid,
            nodeName: nodeInfo.name,
            nodeType: nodeInfo.type as FlowNodeTypes,
            testCases: [],
            isExpanded: false,
            status: EvalStatus.Skipped,
            runId: eventRunId,
            skipReason: reason,
          }
          state.evalResults.nodes.push(node)
        } else {
          // Update existing node to skipped status
          node.status = EvalStatus.Skipped
          node.skipReason = reason
        }

        updateSummary(state.evalResults)
        agentLogger.debug('Node marked as skipped', {
          nodeUuid: node_uuid,
          reason,
          runId: eventRunId,
        })
      }
    })
  },

  setSSEController: (controller) => set({ sseController: controller }),

  cancelEval: () => {
    set((state) => {
      if (state.sseController) {
        // state.sseController.abort()
        agentLogger.info('Eval SSE stream cancelled')
      }
      state.isEvalRunning = false
      state.sseController = null
    })
  },

  clearEvalStream: () => {
    // Clear all node highlighting when clearing eval stream
    highlightNodeAndEdges([], [])

    // Clear eval badges and scores from all nodes
    const { flowInstance } = useFlowStore.getState()
    if (flowInstance) {
      const { setNodes } = flowInstance
      setNodes((nodes) => updateEvalStatusOnNodes(nodes))
    }

    set({
      events: [],
      evalResults: {
        nodes: [],
        summary: {
          totalTests: 0,
          passed: 0,
          failed: 0,
          testing: 0,
        },
      },
      isEvalRunning: false,
      isPaused: false,
      currentEvalId: null,
      currentRunId: null,
      currentlyTestingNodeUuid: null,
    })
  },

  setIsEvalRunning: (isRunning) => set({ isEvalRunning: isRunning }),

  toggleNodeExpanded: (nodeUuid, runId = null) => {
    set((state) => {
      const node = state.evalResults.nodes.find(
        (n) => n.nodeUuid === nodeUuid && (runId === null || n.runId === runId),
      )

      if (!node && runId !== null) {
        // Fallback to matching by nodeUuid if runId-specific match not found
        const fallbackNode = state.evalResults.nodes.find((n) => n.nodeUuid === nodeUuid)
        if (fallbackNode) {
          fallbackNode.isExpanded = !fallbackNode.isExpanded
        }
        return
      }

      if (node) {
        node.isExpanded = !node.isExpanded
      }
    })
  },

  reset: () => {
    set((state) => {
      // Abort SSE stream if active
      if (state.sseController) {
        state.sseController.abort()
        agentLogger.info('Eval SSE stream aborted on reset')
      }

      // Cancel backend eval if running
      if (state.currentEvalId) {
        flowService
          .cancelEval(state.currentEvalId)
          .then(() => {
            agentLogger.info('Backend eval cancelled on reset', { evalId: state.currentEvalId })
          })
          .catch((error: unknown) => {
            agentLogger.info('Failed to cancel backend eval on reset', {
              evalId: state.currentEvalId,
              error,
            })
          })
      }

      return { ...initialState }
    })
  },
}))
